import React, { useEffect, useMemo, useState } from "react";
import API from "../api/api";
import Seo from "../components/Seo";
import "./ProfilePage.css";

const formatDate = (dateStr) => {
    if (!dateStr) return "Не указана";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ru-RU");
};

export default function ProfilePage() {
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await API.get("/users/profile/");
                setProfileData(res.data);
            } catch (err) {
                console.error("Ошибка загрузки профиля:", err);
                setError("Не удалось загрузить профиль");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const profileStats = useMemo(() => {
        if (!profileData) return null;

        const {
            tariff_plan,
            main_organization,
            additional_organizations,
            government_institutions,
        } = profileData;

        const tariffName = tariff_plan?.name || "Не назначен";

        const totalBins =
            (main_organization ? 1 : 0) + (additional_organizations?.length || 0);

        const hasUnlimitedBins =
            tariff_plan?.bin_limit === null || tariff_plan?.bin_limit === undefined
                ? !!tariff_plan
                : false;

        const tariffBinLimit = hasUnlimitedBins ? null : tariff_plan?.bin_limit;

        const remainingBins =
            tariffBinLimit !== null && tariffBinLimit !== undefined
                ? tariffBinLimit - totalBins
                : null;

        const isBinLimitReached =
            remainingBins !== null && remainingBins <= 0;

        const isBinLimitNear =
            remainingBins !== null && remainingBins === 1;

        const organizationsCount =
            (main_organization ? 1 : 0) + (additional_organizations?.length || 0);

        const govCount = government_institutions?.length || 0;

        return {
            tariffName,
            totalBins,
            hasUnlimitedBins,
            tariffBinLimit,
            remainingBins,
            isBinLimitReached,
            isBinLimitNear,
            organizationsCount,
            govCount,
        };
    }, [profileData]);

    if (loading) {
        return (
            <>
                <Seo
                    title="Профиль – Qarzhy"
                    description="Личный кабинет пользователя Qarzhy"
                    url="https://qarzhy.kz/profile"
                    noindex={true}
                />

                <div className="profile-page">
                    <div className="profile-loading-card">
                        <div className="profile-loading-spinner" />
                        <div className="profile-loading-text">Загрузка профиля...</div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Seo
                    title="Профиль – Qarzhy"
                    description="Личный кабинет пользователя Qarzhy"
                    url="https://qarzhy.kz/profile"
                    noindex={true}
                />

                <div className="profile-page">
                    <div className="profile-state-card profile-state-card--error">
                        {error}
                    </div>
                </div>
            </>
        );
    }

    if (!profileData) {
        return (
            <>
                <Seo
                    title="Профиль – Qarzhy"
                    description="Личный кабинет пользователя Qarzhy"
                    url="https://qarzhy.kz/profile"
                    noindex={true}
                />

                <div className="profile-page">
                    <div className="profile-state-card">
                        Данные профиля отсутствуют
                    </div>
                </div>
            </>
        );
    }

    const {
        username,
        email,
        subscription_start,
        subscription_end,
        subscription_active,
        days_left,
        main_organization,
        additional_organizations,
        government_institutions,
    } = profileData;

    const {
        tariffName,
        totalBins,
        hasUnlimitedBins,
        tariffBinLimit,
        remainingBins,
        isBinLimitReached,
        isBinLimitNear,
        organizationsCount,
        govCount,
    } = profileStats;

    const daysLeftClass =
        days_left !== null &&
        days_left !== undefined &&
        days_left <= 7
            ? "profile-days-left profile-days-left--danger"
            : "profile-days-left profile-days-left--ok";

    return (
        <>
            <Seo
                title="Профиль – Qarzhy"
                description="Личный кабинет пользователя Qarzhy"
                url="https://qarzhy.kz/profile"
                noindex={true}
            />

            <div className="profile-page">
                <section className="profile-hero-card">
                    <div className="profile-hero-badge">Личный кабинет</div>

                    <div className="profile-hero-head">
                        <div>
                            <h1 className="profile-page__title">Профиль пользователя</h1>
                            <p className="profile-page__subtitle">
                                Информация об учетной записи, подписке и подключенных организациях.
                            </p>
                        </div>

                        <div className="profile-hero-status-wrap">
                            <span
                                className={
                                    subscription_active
                                        ? "profile-badge profile-badge--active"
                                        : "profile-badge profile-badge--inactive"
                                }
                            >
                                {subscription_active ? "Подписка активна" : "Подписка неактивна"}
                            </span>
                        </div>
                    </div>

                    <div className="profile-stats-grid">
                        <div className="profile-stat-card">
                            <span className="profile-stat-label">Тариф</span>
                            <strong>{tariffName}</strong>
                        </div>

                        <div className="profile-stat-card">
                            <span className="profile-stat-label">Организаций</span>
                            <strong>{organizationsCount}</strong>
                        </div>

                        <div className="profile-stat-card">
                            <span className="profile-stat-label">Гос. учреждений</span>
                            <strong>{govCount}</strong>
                        </div>

                        <div className="profile-stat-card profile-stat-card--accent">
                            <span className="profile-stat-label">Осталось дней</span>
                            <strong>{days_left ?? "—"}</strong>
                        </div>
                    </div>
                </section>

                <div className="profile-account-grid">
                    <section className="profile-card profile-card--compact">
                        <div className="profile-card-kicker">Имя пользователя</div>
                        <div className="profile-account-value">
                            {username || "Не указано"}
                        </div>
                    </section>

                    <section className="profile-card profile-card--compact">
                        <div className="profile-card-kicker">Email</div>
                        <div className="profile-account-value">
                            {email || "Не указан"}
                        </div>
                    </section>
                </div>

                <section className="profile-card">
                    <div className="profile-card-header">
                        <div>
                            <div className="profile-card-kicker">Подписка</div>
                        </div>
                    </div>

                    <div className="profile-subscription-grid">
                        <div className="profile-info-block">
                            <div className="profile-info-row">
                                <div className="profile-info-label">Тариф</div>
                                <div className="profile-info-value">{tariffName}</div>
                            </div>

                            <div className="profile-info-row">
                                <div className="profile-info-label">Период</div>
                                <div className="profile-info-value">
                                    {formatDate(subscription_start)} — {formatDate(subscription_end)}
                                </div>
                            </div>

                            <div className="profile-info-row">
                                <div className="profile-info-label">БИНы</div>
                                <div className="profile-info-value">
                                    <span className="profile-bins-usage">
                                        {hasUnlimitedBins
                                            ? `${totalBins} / Без ограничений`
                                            : `${totalBins} / ${tariffBinLimit ?? "Не указан"}`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="profile-info-block">
                            <div className="profile-info-row">
                                <div className="profile-info-label">Статус</div>
                                <div className="profile-info-value">
                                    <span
                                        className={
                                            subscription_active
                                                ? "profile-badge profile-badge--active"
                                                : "profile-badge profile-badge--inactive"
                                        }
                                    >
                                        {subscription_active ? "Активна" : "Неактивна"}
                                    </span>
                                </div>
                            </div>

                            <div className="profile-info-row">
                                <div className="profile-info-label">Осталось</div>
                                <div className="profile-info-value">
                                    <span className={daysLeftClass}>
                                        {days_left !== null && days_left !== undefined
                                            ? `${days_left} дней`
                                            : "Не рассчитано"}
                                    </span>
                                </div>
                            </div>

                            <div className="profile-info-row">
                                <div className="profile-info-label">Свободно</div>
                                <div className="profile-info-value">
                                    {hasUnlimitedBins
                                        ? "Без ограничений"
                                        : remainingBins !== null && remainingBins !== undefined
                                            ? remainingBins
                                            : "Не рассчитано"}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-messages">
                        {hasUnlimitedBins && (
                            <div className="profile-hint">
                                Без ограничений по количеству БИНов.
                            </div>
                        )}

                        {!hasUnlimitedBins && isBinLimitNear && (
                            <div className="profile-hint">
                                Остался 1 слот для БИНа.
                            </div>
                        )}

                        {!hasUnlimitedBins && isBinLimitReached && (
                            <div className="profile-warning">
                                Достигнут лимит БИНов по текущему тарифу.
                            </div>
                        )}

                        {days_left !== null && days_left !== undefined && days_left <= 20 && (
                            <div className="profile-warning">
                                Подписка истекает через {days_left} дней.
                            </div>
                        )}
                    </div>
                </section>

                <section className="profile-card profile-card--full">
                    <div className="profile-card-header">
                        <div>
                            <div className="profile-card-kicker">Организации</div>
                        </div>
                    </div>

                    <div className="profile-section-block">
                        <div className="profile-section-title-row">
                            <h3>Основная организация</h3>
                        </div>

                        {main_organization ? (
                            <div className="profile-table-wrap">
                                <table className="profile-table">
                                    <thead>
                                        <tr>
                                            <th>№</th>
                                            <th>Наименование</th>
                                            <th>БИН</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>{main_organization.name}</td>
                                            <td>{main_organization.bin}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="profile-empty">Не указана</p>
                        )}
                    </div>

                    <div className="profile-section-block">
                        <div className="profile-section-title-row">
                            <h3>Дополнительные организации</h3>
                        </div>

                        {additional_organizations && additional_organizations.length > 0 ? (
                            <div className="profile-table-wrap">
                                <table className="profile-table">
                                    <thead>
                                        <tr>
                                            <th>№</th>
                                            <th>Наименование</th>
                                            <th>БИН</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {additional_organizations.map((org, index) => (
                                            <tr key={org.id}>
                                                <td>{index + 1}</td>
                                                <td>{org.name}</td>
                                                <td>{org.bin}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="profile-empty">Дополнительные организации отсутствуют</p>
                        )}
                    </div>

                    <div className="profile-section-block">
                        <div className="profile-section-title-row">
                            <h3>Государственные учреждения</h3>
                        </div>

                        {government_institutions && government_institutions.length > 0 ? (
                            <div className="profile-table-wrap">
                                <table className="profile-table">
                                    <thead>
                                        <tr>
                                            <th>№</th>
                                            <th>Наименование</th>
                                            <th>Код ГУ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {government_institutions.map((gi, index) => (
                                            <tr key={gi.id}>
                                                <td>{index + 1}</td>
                                                <td>{gi.name}</td>
                                                <td>{gi.gov_code}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="profile-empty">Государственные учреждения отсутствуют</p>
                        )}
                    </div>
                </section>
            </div>
        </>
    );
}